import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const DatabaseInit = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get('/admin/database-status');
      setStatus(response.data);
    } catch (error) {
      setError('Failed to check database status');
      console.error('Error checking database status:', error);
    }
  };

  const initializeDatabase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await axios.post('/admin/initialize-database');
      setInitialized(true);
      
      // Refresh status
      await checkStatus();
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to initialize database');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          
          <div className="text-center mb-8">
            <Database className="h-16 w-16 mx-auto mb-4 text-black" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Crawley Athletic FC Database Setup
            </h1>
            <p className="text-gray-600">
              Initialize the database with essential data for your football club website
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {initialized && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800">Database initialized successfully!</span>
              </div>
            </div>
          )}

          {status && status.needs_initialization ? (
            <div className="mb-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                  Database Initialization Required
                </h3>
                <p className="text-yellow-700 mb-4">
                  Your database is empty. Initialize it with essential data:
                </p>
                <ul className="text-yellow-700 space-y-2 mb-4">
                  <li>• Admin User: username: admin, password: CAFC4.0</li>
                  <li>• Sample Products: Home Kit, Training Shirt, Club Scarf</li>
                  <li>• Sample Fixtures: Upcoming matches</li>
                  <li>• Sample News: Welcome posts</li>
                </ul>
              </div>

              <button
                onClick={initializeDatabase}
                disabled={loading}
                className="w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 disabled:bg-gray-400"
              >
                {loading ? 'Initializing Database...' : 'Initialize Database'}
              </button>
            </div>
          ) : status && !status.needs_initialization ? (
            <div className="text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Database Ready
                </h3>
                <p className="text-green-700">
                  Database contains {status.users_count} users and {status.products_count} products.
                </p>
              </div>
              
              <div className="space-y-3">
                <a
                  href="/admin"
                  className="block w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 text-center"
                >
                  Go to Admin Panel
                </a>
                <a
                  href="/"
                  className="block w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 text-center"
                >
                  View Website
                </a>
              </div>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
              <p className="text-gray-600">Setting up your database...</p>
            </div>
          ) : null}

        </div>
      </div>
    </div>
  );
};

export default DatabaseInit;